import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { NovaCompanionChatService } from "./nova-companion-chat.service";
import { NovaRealtimeRelayService } from "./nova-realtime-relay.service";
import { NovaRealtimeSessionService } from "./nova-realtime-session.service";
import {
  NovaRealtimeEventRequest,
  NovaRealtimeFunctions,
  NovaRealtimeServerEvent,
  NovaWalkthroughEvent,
  NovaWalkthroughState,
  NovaWalkthroughStep,
  walkthroughStepLocation,
} from "./nova-realtime.model";

/** Not relayed, so not in NovaRealtimeRelayedEventTypes - read here the same way the relay reads
 *  its own bookkeeping-only event names (OUTPUT_AUDIO_STARTED and friends). */
const RESPONSE_CREATED = "response.created";
const RESPONSE_DONE = "response.done";

/**
 * Route prefixes the NavigationEnd match rule resolves against, to decide whether a navigation
 * crossed INTO the next step's location. The value is a location key in the sense of
 * `walkthroughStepLocation`: an area name for the five app areas, and the route itself for the
 * pages outside them, which is how the catalogue keys those steps.
 *
 * Deliberately a SEPARATE small table rather than a shared import: `NovaNavigationContextService`'s
 * is a private implementation detail, and the two tables answering the same question is a smaller
 * risk than this feature reaching into another feature's internals for a handful of string pairs
 * that rarely change. Worth extracting into one shared helper if a third caller ever needs it.
 *
 * `/help` is listed although the app has no such route yet (the catalogue's route is provisional):
 * if one is added under that path, Help walkthroughs start matching without another change here.
 */
const LOCATION_ROUTE_PREFIXES: ReadonlyArray<readonly [string, string]> = [
  ["/score", "Score"],
  ["/plan-home", "Plan"],
  ["/plan", "Plan"],
  ["/sanctuary", "Sanctuary"],
  ["/learn", "Learn"],
  ["/landing-page", "Home"],
  ["/community", "/community"],
  ["/settings", "/settings"],
  ["/help", "/help"],
];

type NovaWalkthroughStatus = "idle" | "active" | "paused" | "ended";

/**
 * What asking for the next step did. `armed`: the app had seen the user reach it. `affirmed`: nothing
 * was armed, so the user's word that they finished was taken and the next step in the held slice was
 * moved to. `other_screen`: the next step is on a screen this slice does not cover, so it is reported
 * but not moved to - the app advances when the user gets there. `none`: nothing to move to.
 */
type NextStepOutcome =
  | { kind: "armed" }
  | { kind: "affirmed"; step: NovaWalkthroughStep }
  | { kind: "other_screen"; step: NovaWalkthroughStep }
  | { kind: "none" };

/**
 * B6. The active walkthrough's transient progression - the client half of the hybrid split (Nova
 * Architecture Brief §3; Handoff §3 "Client owns for the active walkthrough"). Everything here
 * vanishes with the session; nothing here is canonical or durable - that's `IWalkthroughCatalogue`,
 * server-side.
 *
 * OWNS: which step is current, whether the next one is armed, pause/off-track/end state, and the
 * five walkthrough tool calls that have no server handler (see `NovaRealtimeRelayService`'s
 * `CLIENT_HANDLED_FUNCTIONS`) - `next_step`/`pause_walkthrough`/`resume_walkthrough`/`skip_to`/
 * `end_walkthrough` are all answered from here, entirely locally, which is what makes "ordinary
 * progression costs no round trip" (the Handoff's own phrase) actually true.
 *
 * SCOPE, DELIBERATELY NARROWER THAN THE FULL HANDOFF. The Handoff's own "Conversational controls"
 * step also covers transcript-affirmation matching and a manual Next-step UI control; neither is
 * built here - this piece is exactly what the B6 punch list's own wording asks for: step state,
 * the NavigationEnd match rule, prefetch-next-area, pause/resume/skip/end, and the race handling
 * those need. A PRACTICAL CONSEQUENCE: the NavigationEnd match rule only arms a step whose LOCATION
 * (area or route) differs from the current one - it has no way to tell that an IN-SCREEN action (add a Star, save
 * a Plan) completed a step, so a walkthrough whose steps stay within one area cannot progress past
 * its first step from this alone. That is a real, known gap, not a bug - closing it is
 * transcript-affirmation / the Next-step control, explicitly out of scope here.
 */
@Injectable({ providedIn: "root" })
export class NovaWalkthroughService implements OnDestroy {
  private status: NovaWalkthroughStatus = "idle";
  private walkthroughId: string | null = null;
  private area: string | null = null;
  private nextArea: string | null = null;
  private steps: NovaWalkthroughStep[] = [];
  /** Index into `steps` of the step most recently DELIVERED to Nova (spoken or armed-then-spoken). */
  private stepIndex = 0;

  /**
   * The step most recently delivered to Nova, and the one armed after it. Tracked as ids alongside
   * the index because a typed turn tells the server where the user is by id, and across a slice
   * boundary the current step is the last one of a slice that is no longer held.
   */
  private currentStepId: string | null = null;
  private armedStepId: string | null = null;

  /**
   * How this walkthrough began. A spoken one injects an armed step into the live conversation and
   * fetches the next slice through the voice relay; a typed one has neither, so the armed step is
   * held here until the next message sends it (as an id) and the slice comes from the chat API.
   */
  private source: "voice" | "text" = "voice";

  /**
   * The next step's text, injected silently and waiting for `next_step()` to let Nova speak from
   * it. Null when nothing is armed - the ordinary state between steps until a completion signal
   * fires.
   */
  private armedText: string | null = null;

  /**
   * True between a response starting and finishing. Gates `sendWalkthroughStep`: the Handoff
   * (§8, "Race handling") asks that this never fires while a response is active, so an arm that
   * would otherwise land mid-turn is queued in `pendingArmText` and flushed on `response.done`
   * instead of sent immediately.
   */
  private responseActive = false;
  private pendingArmText: string | null = null;

  /**
   * The user turn (`relay.currentTurnId`) in which the model was last given a step by begin_walkthrough,
   * next_step or skip_to. The user's word that they have finished is what moves a walkthrough on, and
   * they have not spoken again within the same turn, so a further un-armed next_step or skip_to in it
   * is the model running ahead and is refused. A step the APP armed is different: it saw the user
   * reach it, which is evidence, so it is exempt.
   */
  private stepTurnId: number | null = null;

  /** An area already fetched ahead of need, held until the boundary is actually crossed. */
  private prefetched: { area: string; steps: NovaWalkthroughStep[]; nextArea: string | null } | null = null;
  private prefetchInFlight = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private router: Router,
    private session: NovaRealtimeSessionService,
    private relay: NovaRealtimeRelayService,
    private chat: NovaCompanionChatService
  ) {
    this.subscriptions.add(
      this.relay.responses$.subscribe((exchange) => {
        if (
          exchange.request.functionName === NovaRealtimeFunctions.BeginWalkthrough &&
          exchange.response?.walkthroughSteps?.length
        ) {
          this.onBegin(exchange.request, exchange.response.walkthroughSteps, exchange.response.walkthroughNextArea ?? null);
        }
      })
    );

    this.subscriptions.add(
      this.relay.clientHandledFunctionCalls$.subscribe((request) => this.onClientHandledCall(request))
    );

    this.subscriptions.add(this.session.events$.subscribe((event) => this.onSessionEvent(event)));

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => this.onNavigationEnd(event))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Whether a walkthrough is currently running (active or paused - not idle or ended). */
  get isActive(): boolean {
    return this.status === "active" || this.status === "paused";
  }

  /**
   * A typed turn began a walkthrough: the same side-band a spoken `begin_walkthrough` produces,
   * arriving on the reply's final frame instead of over the relay. Step 0 was already given to the
   * model in the tool's own output, so this only sets up to track everything after it.
   */
  beginFromChat(steps: NovaWalkthroughStep[], nextArea: string | null): void {
    if (!steps.length) {
      return;
    }

    this.startWalkthrough(steps[0].walkthrough ?? null, steps, nextArea, "text");
  }

  /**
   * Where the user has got to, for a typed message to carry. Null when there is no walkthrough to
   * report - idle, ended, or one that has not yet delivered a step.
   */
  snapshot(): NovaWalkthroughState | null {
    if (!this.isActive || !this.walkthroughId || !this.currentStepId) {
      return null;
    }

    return {
      walkthroughId: this.walkthroughId,
      currentStepId: this.currentStepId,
      armedStepId: this.armedStepId,
      paused: this.status === "paused",
    };
  }

  /**
   * Applies what the model asked of the walkthrough in a typed turn. The server only checked the
   * request against the state this service reported; the state itself is changed here, by the same
   * transitions the spoken tools use.
   */
  applyEvents(events: NovaWalkthroughEvent[] | undefined): void {
    for (const event of events ?? []) {
      switch (event.type) {
        case NovaRealtimeFunctions.NextStep:
          this.doNextStep();
          break;
        case NovaRealtimeFunctions.PauseWalkthrough:
          this.doPause();
          break;
        case NovaRealtimeFunctions.ResumeWalkthrough:
          this.doResume();
          break;
        case NovaRealtimeFunctions.SkipTo:
          this.doSkip();
          break;
        case NovaRealtimeFunctions.EndWalkthrough:
          this.doEnd();
          break;
        default:
          break;
      }
    }
  }

  /**
   * `begin_walkthrough` resolved. Step 0 was already answered directly in the call's own
   * `functionCallOutput` (the ordinary speaking path) - this only takes the side-band and sets up
   * to track everything AFTER it.
   */
  private onBegin(
    request: NovaRealtimeEventRequest,
    steps: NovaWalkthroughStep[],
    nextArea: string | null
  ): void {
    let walkthroughId = steps[0]?.walkthrough ?? null;
    try {
      walkthroughId = JSON.parse(request.arguments ?? "{}")?.topic ?? walkthroughId;
    } catch {
      // The steps themselves already carry the walkthrough id; the request's own topic argument
      // is only a nicer source when it parses.
    }

    this.startWalkthrough(walkthroughId, steps, nextArea, "voice");
    this.stepTurnId = request.turnId ?? this.relay.currentTurnId;
  }

  private stepGivenThisTurn(): boolean {
    return this.stepTurnId !== null && this.stepTurnId === this.relay.currentTurnId;
  }

  private startWalkthrough(
    walkthroughId: string | null,
    steps: NovaWalkthroughStep[],
    nextArea: string | null,
    source: "voice" | "text"
  ): void {
    this.source = source;
    this.stepTurnId = null;
    this.currentStepId = steps[0]?.id ?? null;
    this.armedStepId = null;
    this.status = "active";
    this.walkthroughId = walkthroughId;
    this.area = steps[0] ? walkthroughStepLocation(steps[0]) : null;
    this.nextArea = nextArea;
    this.steps = steps;
    this.stepIndex = 0;
    this.armedText = null;
    this.pendingArmText = null;
    this.prefetched = null;
    this.prefetchInFlight = false;

    // eslint-disable-next-line no-console
    console.debug(
      "[nova-walkthrough] began %s in %s (%d steps, next area %s)",
      this.walkthroughId,
      this.area,
      steps.length,
      nextArea ?? "(none)"
    );

    // Step 0 is current immediately (delivered by begin_walkthrough's own answer, not armed here)
    // - if the held area is only one step long, it is ALREADY the last one, and next_step()'s
    // transition into the next area needs the prefetch to already be running.
    this.maybePrefetchIfLastStep(0);
  }

  /**
   * The NavigationEnd match rule: arms the next step when navigation crosses into the AREA that
   * step belongs to. See the class doc comment for why this is the only completion signal built
   * here.
   */
  private onNavigationEnd(event: NavigationEnd): void {
    if (this.status !== "active") {
      return;
    }

    const nextIndex = this.stepIndex + 1;
    const nextStep = this.steps[nextIndex];
    if (!nextStep) {
      // The held area's last step is already current - nothing left to arm until the boundary
      // crossing below hands over the prefetched area.
      this.onAreaBoundaryNavigation(event);
      return;
    }

    // A step in the global overlay has the location "overlay", which no navigation ever produces,
    // so it is never armed from here - the same known gap as an in-screen action.
    const navigatedLocation = this.locationOf(event.urlAfterRedirects);
    if (!navigatedLocation || navigatedLocation !== walkthroughStepLocation(nextStep)) {
      return;
    }

    this.arm(nextStep, nextIndex);
  }

  /**
   * Crossing into the prefetched next area: swaps it in as the active slice and arms its first
   * step, the moment a completed prefetch and a matching navigation coincide.
   */
  private onAreaBoundaryNavigation(event: NavigationEnd): void {
    if (!this.prefetched) {
      return;
    }

    const navigatedLocation = this.locationOf(event.urlAfterRedirects);
    if (!navigatedLocation || navigatedLocation !== this.prefetched.area) {
      return;
    }

    const firstOfNextArea = this.prefetched.steps[0];
    if (!firstOfNextArea) {
      return;
    }

    this.area = this.prefetched.area;
    this.nextArea = this.prefetched.nextArea;
    this.steps = this.prefetched.steps;
    this.stepIndex = -1; // the first step of the new slice is armed, not yet delivered
    this.prefetched = null;

    this.arm(firstOfNextArea, 0);
  }

  /**
   * Silently injects a step's text, immediately or queued behind an active response, and checks
   * whether arming THIS step is also the moment to start prefetching the area after it - the step
   * being armed now is the one that will be current when its own completion eventually demands
   * the next area, so the fetch has to start here to actually be "ahead of need" by then.
   */
  private arm(step: NovaWalkthroughStep, index: number): void {
    this.armedText = step.instruction;
    this.armedStepId = step.id;

    // Only a spoken walkthrough has a live conversation to inject into. A typed one carries the
    // armed step's id on the next message instead.
    if (this.source === "voice") {
      if (this.responseActive) {
        this.pendingArmText = step.instruction;
      } else {
        this.session.sendWalkthroughStep(step.instruction);
      }
    }

    this.maybePrefetchIfLastStep(index);
  }

  /** Starts the next-area prefetch only when `index` names the held slice's own last step. */
  private maybePrefetchIfLastStep(index: number): void {
    if (index === this.steps.length - 1) {
      this.maybePrefetchNextArea();
    }
  }

  /**
   * Ahead-of-need fetch for the area after the one currently held. Never speaks, never answers a
   * call - see `NovaRealtimeRelayService.prefetchWalkthroughArea`.
   */
  private maybePrefetchNextArea(): void {
    if (!this.nextArea || !this.walkthroughId || this.prefetched || this.prefetchInFlight) {
      return;
    }

    this.prefetchInFlight = true;
    const walkthroughId = this.walkthroughId;
    const area = this.nextArea;

    const fetch =
      this.source === "text"
        ? this.chat.getWalkthroughSlice(walkthroughId, area)
        : this.relay.prefetchWalkthroughArea(walkthroughId, area);

    fetch
      .then((response) => {
        this.prefetchInFlight = false;
        if (response?.walkthroughSteps?.length && this.status === "active") {
          this.prefetched = {
            area,
            steps: response.walkthroughSteps,
            nextArea: response.walkthroughNextArea ?? null,
          };
        }
      })
      .catch(() => {
        this.prefetchInFlight = false;
      });
  }

  /**
   * One of the five client-handled walkthrough tools. Always answered - a call this drops leaves
   * the model blocked for the rest of the session, the same failure `NovaRealtimeToolCallService`
   * exists to prevent on the tools it owns.
   */
  private onClientHandledCall(request: NovaRealtimeEventRequest): void {
    if (!request.callId) {
      return;
    }

    switch (request.functionName) {
      case NovaRealtimeFunctions.NextStep:
        this.onNextStep(request.callId);
        return;
      case NovaRealtimeFunctions.PauseWalkthrough:
        this.onPause(request.callId);
        return;
      case NovaRealtimeFunctions.ResumeWalkthrough:
        this.onResume(request.callId);
        return;
      case NovaRealtimeFunctions.SkipTo:
        this.onSkip(request.callId);
        return;
      case NovaRealtimeFunctions.EndWalkthrough:
        this.onEnd(request.callId);
        return;
      default:
        return;
    }
  }

  /**
   * `next_step()`. When the app armed the step, its TEXT already reached Nova silently, at arm time,
   * and this only unblocks her turn with a minimal ack, so what she says next is in her own words
   * rather than a script this answer hands her. When nothing was armed the user's word is the signal
   * and the step comes back here instead, since she has not been given it. See `sendWalkthroughStep`.
   */
  private onNextStep(callId: string): void {
    if (!this.armedText && this.stepGivenThisTurn()) {
      this.session.sendFunctionCallOutput(
        callId,
        JSON.stringify({
          ok: false,
          reason: "wait_for_user",
          note:
            "You already moved the walkthrough on this turn. Do not repeat the step you just gave or guess the next one - wait for the user to speak again.",
        })
      );
      return;
    }

    const outcome = this.doNextStep();
    if (outcome.kind !== "none") {
      this.stepTurnId = this.relay.currentTurnId;
    }

    const describe = (step: NovaWalkthroughStep) => ({
      id: step.id,
      instruction: step.instruction,
      expect: step.expect,
    });

    let output: unknown;
    switch (outcome.kind) {
      case "armed":
        output = { ok: true };
        break;
      case "affirmed":
        output = { ok: true, step: describe(outcome.step) };
        break;
      case "other_screen":
        output = {
          ok: true,
          step: describe(outcome.step),
          note: "This step is on another screen. The app moves on once the user gets there.",
        };
        break;
      default:
        output = { ok: false, reason: "no_next_step" };
    }

    this.session.sendFunctionCallOutput(callId, JSON.stringify(output));
  }

  /**
   * The transition itself, shared by the spoken tool above and a typed turn's event.
   *
   * THE APP ONLY SEES THE USER MOVE BETWEEN SCREENS, so a step done within one screen (most of them)
   * never arms the next. Without a fallback such a walkthrough stalls at its first step. The user
   * saying they have finished is then taken as the signal, which is what the review's own fallback
   * says; the app still arms the next step when it can see the user get there.
   *
   * The prefetch check belongs at ARM time for an armed step, since it was checked when `arm()` put
   * its text in context. An affirmed step was never armed, so the check happens here.
   */
  private doNextStep(): NextStepOutcome {
    if (this.status !== "active") {
      return { kind: "none" };
    }

    if (this.armedText) {
      this.stepIndex += 1;
      this.armedText = null;
      this.currentStepId = this.armedStepId;
      this.armedStepId = null;
      return { kind: "armed" };
    }

    const following = this.steps[this.stepIndex + 1];
    if (following) {
      this.stepIndex += 1;
      this.currentStepId = following.id;
      this.armedStepId = null;
      this.maybePrefetchIfLastStep(this.stepIndex);
      return { kind: "affirmed", step: following };
    }

    const onAnotherScreen = this.prefetched?.steps[0];
    return onAnotherScreen ? { kind: "other_screen", step: onAnotherScreen } : { kind: "none" };
  }

  private onPause(callId: string): void {
    this.session.sendFunctionCallOutput(callId, JSON.stringify({ ok: this.doPause() }));
  }

  private doPause(): boolean {
    if (this.status !== "active") {
      return false;
    }

    this.status = "paused";
    return true;
  }

  private onResume(callId: string): void {
    this.session.sendFunctionCallOutput(callId, JSON.stringify({ ok: this.doResume() }));
  }

  private doResume(): boolean {
    if (this.status !== "paused") {
      return false;
    }

    this.status = "active";
    return true;
  }

  /**
   * `skip_to()`. Unlike `next_step`, nothing was armed for this - there is no prior "expected
   * completion condition" to have matched - so the step's content goes straight into the ordinary
   * function-call-output-then-speak pairing, the same shape `begin_walkthrough`'s step 0 uses,
   * rather than through the silent arm-then-speak path.
   */
  private onSkip(callId: string): void {
    if (this.stepGivenThisTurn()) {
      this.session.sendFunctionCallOutput(
        callId,
        JSON.stringify({
          ok: false,
          reason: "wait_for_user",
          note:
            "You already moved the walkthrough on this turn. Do not repeat the step you just gave or guess the next one - wait for the user to speak again.",
        })
      );
      return;
    }

    const upcoming = this.doSkip();
    if (!upcoming) {
      this.session.sendFunctionCallOutput(callId, JSON.stringify({ ok: false, reason: "no_next_step" }));
      return;
    }

    this.stepTurnId = this.relay.currentTurnId;

    this.session.sendFunctionCallOutput(
      callId,
      JSON.stringify({ ok: true, step: { id: upcoming.id, instruction: upcoming.instruction, expect: upcoming.expect } })
    );
  }

  private doSkip(): NovaWalkthroughStep | null {
    const upcoming = this.steps[this.stepIndex + 1];
    if (this.status !== "active" || !upcoming) {
      return null;
    }

    this.stepIndex += 1;
    this.armedText = null;
    this.armedStepId = null;
    this.currentStepId = upcoming.id;

    // Delivered directly rather than through `arm()`, so the "is this the last step" prefetch
    // check has to happen here instead - see arm()'s own doc comment for why the check has to
    // happen at the moment a step becomes current, not later.
    this.maybePrefetchIfLastStep(this.stepIndex);
    return upcoming;
  }

  private onEnd(callId: string): void {
    this.doEnd();
    this.session.sendFunctionCallOutput(callId, JSON.stringify({ ok: true }));
  }

  private doEnd(): void {
    this.status = "ended";
    this.walkthroughId = null;
    this.steps = [];
    this.stepIndex = 0;
    this.armedText = null;
    this.armedStepId = null;
    this.currentStepId = null;
    this.pendingArmText = null;
    this.prefetched = null;
  }

  /** Tracks whether a response is in flight, and flushes a queued arm once it finishes. */
  private onSessionEvent(event: NovaRealtimeServerEvent): void {
    if (event.type === RESPONSE_CREATED) {
      this.responseActive = true;
      return;
    }

    if (event.type === RESPONSE_DONE) {
      this.responseActive = false;
      if (this.pendingArmText) {
        const text = this.pendingArmText;
        this.pendingArmText = null;
        this.session.sendWalkthroughStep(text);
      }
    }
  }

  /** Path only, matching NovaNavigationContextService's own normalisation. */
  private locationOf(url: string): string | null {
    const path = url.split("?")[0].split("#")[0];
    const match = LOCATION_ROUTE_PREFIXES.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
    return match ? match[1] : null;
  }
}
